import { TypeChecker } from '@attain-apps/type-guards'

import { ResourceType } from '../resource-type'

import type { Operator } from './operator'
import type { Store } from './store'

/**
 * The closed vocabulary of condition keys.
 *
 * Admission test for a new key, per store it applies to:
 *
 * - `dynamodb` — the attribute is stored on the item
 * - `dms` — it is expressible as a DMS expression over a column that exists
 */
export const REGISTRY = {
  /**
   * A missing owner fails the condition: `createdBy` is optional. A page has
   * no owner of its own and takes its app's.
   *
   * TODO: add `dms` once DMS records have URNs and each model's owner column
   * is mapped
   */
  resourceOwner: {
    appliesTo: {
      dynamodb: [
        ResourceType.Tenant,
        ResourceType.Zone,
        ResourceType.App,
        ResourceType.Scenario,
        ResourceType.Page,
      ],
    },
    description: 'The user who created the resource',
    operators: ['equals'],
  },
} as const satisfies Record<string, Key.Definition>

/** A registered condition key */
export type Key = keyof typeof REGISTRY
export namespace Key {
  /** Describes one entry of {@link REGISTRY} */
  export interface Definition {
    readonly description: string
    /**
     * The resource types that carry this attribute, per store. A store absent
     * here cannot check the key
     */
    readonly appliesTo: Readonly<
      Partial<Record<Store, readonly ResourceType[]>>
    >
    readonly operators: readonly Operator[]
  }

  export const ALL: readonly Key[] = Object.keys(REGISTRY) as Key[]
  export const isKey = TypeChecker.Factory.oneOf(ALL)
}
